/**
 * Types for the Metrics Explorer GraphQL surface.
 *
 * These mirror `uptime/apps/graphql/graphql/metrics/query_metrics_explorer.graphql`.
 * Field names follow the backend's snake_case rather than being camelised, so a series
 * or an analysis parameter reads the same on both sides of the wire.
 */

export const METRIC_GRANULARITIES = [
  "AUTO",
  "ONE_MINUTE",
  "FIFTEEN_MINUTES",
  "ONE_HOUR",
  "ONE_DAY",
  "ONE_WEEK",
  "ONE_MONTH",
] as const;
export type MetricGranularity = (typeof METRIC_GRANULARITIES)[number];

export const METRIC_AGGREGATIONS = ["AVG", "MIN", "MAX", "SUM", "COUNT", "MEDIAN", "P75", "P90", "P95", "P99"] as const;
export type MetricAggregation = (typeof METRIC_AGGREGATIONS)[number];

export const METRIC_COMPARISON_PERIODS = [
  "PREVIOUS_PERIOD",
  "PREVIOUS_DAY",
  "PREVIOUS_WEEK",
  "PREVIOUS_MONTH",
  "PREVIOUS_YEAR",
  "CUSTOM_OFFSET",
] as const;
export type MetricComparisonPeriod = (typeof METRIC_COMPARISON_PERIODS)[number];

/** PRIMARY and COMPARISON are queried; the rest are overlays the analysis engine computed. */
export type MetricSeriesKind =
  | "PRIMARY"
  | "COMPARISON"
  | "TREND"
  | "MOVING_AVERAGE"
  | "BASELINE_UPPER"
  | "BASELINE_LOWER";

export type MetricSeriesErrorCode =
  | "UNKNOWN_DATA_TYPE"
  | "UNKNOWN_METRIC"
  | "UNKNOWN_DIMENSION"
  | "INCOMPATIBLE_CHECKS"
  | "NON_NUMERIC_METRIC"
  | "TIMEOUT"
  | "BACKEND_ERROR";

export type MetricBaselineMethod = "WINDOW" | "WINDOW_ROBUST";
export type MetricTrendDirection = "UP" | "DOWN" | "FLAT";

export interface MetricDatapoint {
  time: string;
  value: number | null;
}

export interface MetricSeriesDimension {
  name: string;
  value: string;
}

export interface MetricSeries {
  key: string;
  ref: string;
  kind: MetricSeriesKind;
  check_id: number;
  data_type: string;
  metric: string;
  label: string;
  unit: string | null;
  aggregation: MetricAggregation;
  dimensions: MetricSeriesDimension[];
  start_date: string;
  end_date: string;
  /** Seconds this series is shifted back from the primary window; 0 for primary series. */
  time_offset_seconds: number;
  datapoints: MetricDatapoint[];
}

export interface MetricSeriesError {
  ref: string;
  code: MetricSeriesErrorCode;
  message: string;
}

export interface MetricAnalysisWindow {
  start: string | null;
  end: string | null;
  datapoint_count: number;
  gap_count: number;
}

export interface MetricTrendAnalysis {
  slope_per_day: number;
  intercept: number;
  r_squared: number;
  direction: MetricTrendDirection;
  pct_change_over_window: number | null;
  /** Standard error of slope_per_day, same units. Null when there were too few points. */
  slope_stderr: number | null;
  /** An interval spanning zero means the direction is not established. */
  slope_ci_low: number | null;
  slope_ci_high: number | null;
  /** Probability of a slope this steep if the metric were really flat. */
  p_value: number | null;
  confidence_level: number;
}

export interface MetricBaselineAnalysis {
  method: MetricBaselineMethod;
  mean: number;
  stddev: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  sample_count: number;
  /** p75 - p25. */
  iqr: number;
}

export interface MetricAnomalyAnalysis {
  current_zscore: number | null;
  max_zscore: number;
  outlier_count: number;
  outlier_ratio: number;
  last_outlier_at: string | null;
}

export interface MetricSeasonalityAnalysis {
  hour_of_day: (number | null)[];
  day_of_week: (number | null)[];
  /** [weekday][hour], 7 rows of 24. Feeds the seasonality heatmap. */
  hour_by_weekday: (number | null)[][];
  strength: number;
  peak_hour: number | null;
  trough_hour: number | null;
  timezone: string;
}

/** The scalars an alert rule will threshold on. Treat the field names as a contract. */
export interface MetricSeriesAnalysis {
  series_key: string;
  engine_version: string;
  window: MetricAnalysisWindow;
  trend: MetricTrendAnalysis | null;
  baseline: MetricBaselineAnalysis | null;
  anomaly: MetricAnomalyAnalysis | null;
  seasonality: MetricSeasonalityAnalysis | null;
  outliers: MetricDatapoint[];
}

export interface MetricsExplorerMeta {
  granularity: MetricGranularity;
  total_datapoints: number;
  truncated: boolean;
  query_count: number;
  query_time_ms: number;
  used_aggregate_tables: boolean;
}

export interface MetricsExplorerResult {
  series: MetricSeries[];
  analysis: MetricSeriesAnalysis[];
  errors: MetricSeriesError[];
  meta: MetricsExplorerMeta;
}

export interface MetricDefinition {
  name: string;
  type: string;
  label: string;
  unit: string | null;
  numeric: boolean;
}

export interface MetricDimensionDef {
  name: string;
  type: string;
  label: string;
}

export interface MetricDataTypeDef {
  name: string;
  dimensions: MetricDimensionDef[];
  metrics: MetricDefinition[];
}

export interface MetricCheckTypeDef {
  check_type: string;
  data_types: MetricDataTypeDef[];
}

export interface MetricsExplorerSchema {
  version: string;
  check_types: MetricCheckTypeDef[];
}

export interface MetricDimensionValues {
  dimension: string;
  values: string[];
}

export interface MetricDimensionFilterInput {
  dimension: string;
  values: string[];
}

export interface MetricSeriesSpecInput {
  ref: string;
  check_ids: number[];
  data_type?: string;
  metrics?: string[];
  aggregation?: MetricAggregation;
  group_by?: string[];
  filters?: MetricDimensionFilterInput[];
}

export interface MetricComparisonInput {
  period: MetricComparisonPeriod;
  offset_seconds?: number;
}

export interface MetricAnalysisInput {
  trend?: boolean;
  baseline?: boolean;
  anomaly?: boolean;
  seasonality?: boolean;
  moving_average?: boolean;
  moving_average_window?: number;
  baseline_method?: MetricBaselineMethod;
  anomaly_sigma?: number;
  timezone?: string;
}

export interface MetricsExplorerQueryInput {
  start_date: string;
  end_date: string;
  granularity?: MetricGranularity;
  series: MetricSeriesSpecInput[];
  comparison?: MetricComparisonInput | null;
  analysis?: MetricAnalysisInput | null;
}

export interface MetricsExplorerView {
  id: number;
  name: string;
  description: string;
  /** The same document the explorer keeps in its URL. */
  config: Record<string, unknown>;
  is_pinned: boolean;
  created_by_name: string | null;
  created_at: string;
  modified_at: string;
}

export interface MetricsExplorerViewInput {
  name: string;
  description?: string;
  config: Record<string, unknown>;
  is_pinned?: boolean;
}

export type MetricsExplorerExportFormat = "PDF" | "XLS";

export interface MetricsExplorerFile {
  filename: string;
  content_type: string;
  /** base64 */
  data: string;
  truncated: boolean;
}

export interface MetricsExplorerCheckNameInput {
  check_id: number;
  name: string;
}

export interface MetricsExplorerExportInput {
  query: MetricsExplorerQueryInput;
  format: MetricsExplorerExportFormat;
  chart_svg?: string | null;
  check_names?: MetricsExplorerCheckNameInput[];
}
